/** Reine Logik des Konfliktschutzes (Spec 2026-09-26-save-conflict-merge), ohne Vue. */

export interface MergePlan {
  /** Schluessel, die ich gegenueber der Basis geaendert habe. */
  mine: string[]
  /** Schluessel, die sich seit der Basis im gespeicherten Stand geaendert haben. */
  theirs: string[]
  /** mine ∩ theirs, bei denen meine Fassung von der gespeicherten abweicht. */
  conflicts: string[]
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

/**
 * Ein Absatz ohne Inhalt gilt als leer (blockId wurde bereits entfernt). Annahme: Absaetze
 * tragen ausser der technischen blockId keine weiteren attrs; fuegt eine Extension echte
 * Absatz-attrs hinzu (z.B. textAlign), muss die Leerheitspruefung diese beruecksichtigen,
 * sonst wuerde eine solche Aenderung an einem leeren Absatz als "unveraendert" gelten.
 */
function isEmptyParagraph(node: unknown): boolean {
  if (!isPlainObject(node) || node.type !== "paragraph") return false
  const content = node.content
  return content == null || (Array.isArray(content) && content.length === 0)
}

/** Ein TipTap-Doc ohne Inhalt oder nur mit leeren Absaetzen gilt als leer. */
function isEmptyDoc(node: Record<string, unknown>): boolean {
  if (node.type !== "doc") return false
  const content = node.content
  if (content == null) return true
  return Array.isArray(content) && content.every(isEmptyParagraph)
}

/**
 * Normalisiert rekursiv fuer den Vergleich: entfernt `attrs.blockId` (und ein dadurch
 * leeres `attrs`), behandelt leere TipTap-Docs wie `null`, und `null`/`undefined` als gleich.
 */
function normalize(value: unknown): unknown {
  if (value === undefined || value === null) return null
  if (Array.isArray(value)) return value.map(normalize)
  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value)) {
      if (key === "attrs" && isPlainObject(val)) {
        const { blockId: _blockId, ...rest } = val
        if (Object.keys(rest).length > 0) result.attrs = normalize(rest)
        continue
      }
      result[key] = normalize(val)
    }
    return isEmptyDoc(result) ? null : result
  }
  return value
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(normalize(a)) === JSON.stringify(normalize(b))
}

function keysOf(...objects: object[]): string[] {
  return [...new Set(objects.flatMap((o) => Object.keys(o)))]
}

export function planMerge(base: object, mine: object, current: object): MergePlan {
  const b = base as Record<string, unknown>
  const m = mine as Record<string, unknown>
  const c = current as Record<string, unknown>
  const keys = keysOf(b, m, c)
  const mineKeys = keys.filter((k) => !same(b[k], m[k]))
  const theirsKeys = keys.filter((k) => !same(b[k], c[k]))
  const conflicts = mineKeys.filter((k) => theirsKeys.includes(k) && !same(m[k], c[k]))
  return { mine: mineKeys, theirs: theirsKeys, conflicts }
}

/** Gespeicherter Stand, darueber meine Schluessel — ausser denen in keepTheirs. */
export function applyMerge<T extends object>(mine: T, current: T, mineKeys: string[], keepTheirs: ReadonlySet<string>): T {
  const merged = JSON.parse(JSON.stringify(current)) as Record<string, unknown>
  const m = mine as Record<string, unknown>
  for (const k of mineKeys) {
    if (keepTheirs.has(k)) continue
    merged[k] = m[k] === undefined ? undefined : JSON.parse(JSON.stringify(m[k]))
  }
  return merged as T
}
